import { type IRemoteNotification, useNotification } from '@magicbell/react-headless';
import type * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import type { NotificationMetadata } from '@gitbook/app-notifications';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    renderIcon,
} from '@gitbook/spine-react';

import { NotificationFormattedTitle } from './NotificationFormattedTitle';
import { NotificationIcon } from './NotificationIcon';
import { useOpenNotification } from './useOpenNotification';

/**
 * Render a notification
 */
export function NotificationItem(props: { notification: IRemoteNotification }): React.ReactElement {
    const { notification: remoteNotification } = props;
    const notification = useNotification(remoteNotification);
    const openNotification = useOpenNotification();

    const handleMarkAsRead = async () => {
        analytics.logEvent(
            analytics.createNotificationEvent(notification, {
                type: 'notification_mark_as_read',
            })
        );

        notification.markAsRead();
    };

    const handleMarkAsUnread = async () => {
        analytics.logEvent(
            analytics.createNotificationEvent(notification, {
                type: 'notification_mark_as_unread',
            })
        );

        notification.markAsUnread();
    };

    const metadata = notification.customAttributes as NotificationMetadata | null;

    return (
        <ListItem
            onPress={() => {
                openNotification(notification);
            }}
        >
            <ListItemIndicator>
                <NotificationIcon metadata={metadata} />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>
                    <NotificationFormattedTitle notification={notification} />
                </ListItemDescription>
                <ListItemDetail>
                    <DateShortRelative
                        value={notification.sentAt?.toDate() || new Date()}
                        onlyDate={false}
                    />
                </ListItemDetail>
            </ListItemBody>
            <ListItemControls>
                <Button
                    iconOnly
                    kind="nested"
                    size="medium"
                    leadingIcon={notification.isRead ? Icon.EyeOff : Icon.Check}
                    label={`Mark as ${notification.isRead ? 'unread' : 'read'}`}
                    onPress={notification.isRead ? handleMarkAsUnread : handleMarkAsRead}
                />
            </ListItemControls>
            {notification.isRead ? null : (
                <ListItemIndicator>
                    {renderIcon(Icon.Dot, { size: 18, className: 'text-upgrade' })}
                </ListItemIndicator>
            )}
        </ListItem>
    );
}
